import React from 'react';
import {
  showMarkupMenu,
  hideMarkupMenu,
} from 'components/pspdf/components/markup-menu/actions';
import { Selection } from 'components/pspdf/components/selection';
import {
  getSelectedAnnotation,
  isClickedOnElementWithId,
} from 'components/pspdf/components/helpers';
import {
  hideNameEditor,
  showNameEditor,
} from 'components/pspdf/components/snippet-name-editor/actions';
import {
  decorationIconIds,
  linkButtonIds,
  markupMenuIconIds,
} from 'components/pspdf/components/markup-menu';
import {
  nameSnippetFormButtonIds,
  snippetFormId,
} from 'components/pspdf/components/snippet-name-editor/components/snippet-name-form';
import { invert, throttle, isNull } from 'lodash';
import { notEqual } from 'utils/js';
import { GLOBAL_LINK_COUNTER_PREFIX } from 'components/pspdf/components/simple-custom-annotations/components/annotation';

function isMarkupDecorationButtonPress(id) {
  return Object.values(decorationIconIds).some((value) => value === id);
}

function isMarkupLinkActionPress(id) {
  return Object.values(linkButtonIds).some((value) => value === id);
}

function isNameButton(event) {
  return event?.nativeEvent?.target?.id === 'Cortlex-Name-Icon';
}

function clickedOutSideOfMarkupMenu(event: any) {
  return (
    !event.nativeEvent.target.closest('.Cortlex-Annotation') &&
    !event.nativeEvent.target.closest('#Cortlex-Container') &&
    !event.nativeEvent.target.closest('#' + snippetFormId)
  );
}

function clickedOnAnnotation(event: any) {
  return event.nativeEvent.target.closest('.Cortlex-Annotation');
}

export function attachMarkupMenuListeners(
  instance,
  selectedAnnotation,
  markupMenuRenderer,
  renderedAtRef,
  {
    onDeleteLink,
    onStartLinking,
    onFinishLinking,
    onGlobalLinkIndicatorClick,
    onTextSnippetSave,
    onTextSnippetDelete,
  },
) {
  function showMenu(selectedAnnotation) {
    return showMarkupMenu({
      selectedAnnotation,
      instance,
      markupMenuRenderer: markupMenuRenderer.current,
    });
  }

  instance.addEventListener('annotations.press', (event) => {
    selectedAnnotation.current = getSelectedAnnotation(event, instance);
    showMenu(selectedAnnotation.current);
  });

  instance.addEventListener('page.press', async (event) => {
    const id = event.nativeEvent.target.id;
    const isMarkupMenuVisible = Boolean(selectedAnnotation.current);

    if (isNameButton(event)) {
      return showNameEditor({
        selectedAnnotation,
        instance,
        callbacks: {
          onTextSnippetSave,
          onTextSnippetDelete,
        },
      });
    }

    if (isClickedOnElementWithId(event, nameSnippetFormButtonIds.edit)) {
      selectedAnnotation.current = getSelectedAnnotation(event, instance);
      return showNameEditor({
        selectedAnnotation,
        instance,
        callbacks: { onTextSnippetSave, onTextSnippetDelete },
      });
    }

    if (isMarkupDecorationButtonPress(id)) {
      return toggleDecorationAnnotation(event, selectedAnnotation, instance);
    }

    if (isMarkupLinkActionPress(id)) {
      if (id === linkButtonIds.link) {
        return handleStartLinkButtonClick(
          selectedAnnotation,
          instance,
          onStartLinking,
          renderedAtRef,
        );
      } else if (id === linkButtonIds.finish) {
        return handleFinishLinkButtonClick(
          selectedAnnotation,
          instance,
          onFinishLinking,
          showMenu,
          renderedAtRef,
        );
      } else if (id === linkButtonIds.delete) {
        return handleDeleteLinkButtonClick(
          selectedAnnotation,
          instance,
          onDeleteLink,
        );
      }
    }

    if (id.includes(GLOBAL_LINK_COUNTER_PREFIX)) {
      if (isNull(selectedAnnotation.current)) {
        selectedAnnotation.current = getSelectedAnnotation(event, instance);
      }

      if (selectedAnnotation.current) {
        return handleGlobalLinkIndicatorClick(
          selectedAnnotation,
          instance,
          onGlobalLinkIndicatorClick,
        );
      } else {
        const annotationId = id.split('-').pop();

        instance.getAnnotations(event.pageIndex).then((annotations) => {
          const linkAnnotation = annotations.find((annotation) => {
            return annotation.toJSON().customData.annotationId === annotationId;
          });

          instance.setSelectedAnnotation(linkAnnotation);

          selectedAnnotation.current = linkAnnotation;

          setTimeout(() => {
            handleGlobalLinkIndicatorClick(
              selectedAnnotation,
              instance,
              onGlobalLinkIndicatorClick,
            );
          }, 200);
        });
      }
    }

    // If user clicks on another annotation while markup menu is visible
    if (isMarkupMenuVisible && clickedOnAnnotation(event)) {
      selectedAnnotation.current = getSelectedAnnotation(event, instance);

      return showMenu(selectedAnnotation.current);
    }

    // If the user click on anything that is not an annotation,
    // the markup menu or the name editor customOverlay, clear
    // the currently selected element (annotation or text selection)
    if (isMarkupMenuVisible && clickedOutSideOfMarkupMenu(event)) {
      selectedAnnotation.current = null;
      hideMarkupMenu({ instance });
    }
  });

  instance.addEventListener(
    'textSelection.change',
    throttle(async (textSelection) => {
      if (
        textSelection &&
        notEqual(selectedAnnotation.current, textSelection)
      ) {
        selectedAnnotation.current = textSelection;
        showMenu(selectedAnnotation.current);
      }
    }, 500),
  );

  instance.addEventListener('annotations.create', (annotations) => {
    if (selectedAnnotation.current) {
      selectedAnnotation.current = annotations.get(0);
      showMenu(selectedAnnotation.current);
    }
  });

  instance.addEventListener('annotations.update', (annotations) => {
    if (selectedAnnotation.current) {
      selectedAnnotation.current = annotations.get(0);
      showMenu(selectedAnnotation.current);
    }
  });

  instance.addEventListener('annotations.delete', () => {
    if (selectedAnnotation.current) {
      selectedAnnotation.current = null;
      hideMarkupMenu({ instance });
    }
  });
}

async function toggleDecorationAnnotation(event, selectedAnnotation, instance) {
  const decoration = invert(markupMenuIconIds)[event.nativeEvent.target.id];

  const entity = new Selection(selectedAnnotation.current, instance);

  const updatedEntity = await entity.toggleDecoration(decoration);

  // After update check if we need to remove it
  if (updatedEntity.shouldDeleteAnnotation()) {
    return updatedEntity.deleteAnnotation();
  }
}

async function handleStartLinkButtonClick(
  selectedAnnotation,
  instance,
  callback,
  renderedAtRef,
) {
  const selection = new Selection(selectedAnnotation.current, instance);

  await selection.saveAnnotation({
    isLink: true,
    renderedAt: renderedAtRef.current,
  });

  callback && callback(selection.getCustomData());

  hideMarkupMenu({ instance });
  hideNameEditor({ instance });
}

async function handleFinishLinkButtonClick(
  selectedAnnotation,
  instance,
  callback,
  showMenu,
  renderedAtRef,
) {
  const selection = new Selection(selectedAnnotation.current, instance);

  await selection.saveAnnotation({
    isLink: true,
    renderedAt: renderedAtRef.current,
  });

  hideMarkupMenu({ instance });
  hideNameEditor({ instance });

  if (callback) {
    await callback(selection.getCustomData());
    await selection.saveAnnotation({ updatedAt: new Date().toISOString() });
    showMenu(selectedAnnotation.current);
  }
}

async function handleDeleteLinkButtonClick(
  selectedAnnotation,
  instance,
  callback,
) {
  const selection = new Selection(selectedAnnotation.current, instance);
  const instantJSON = await instance.exportInstantJSON();

  await selection.saveAnnotation({ isLink: false });

  callback && callback(selection.getCustomData(), instantJSON);

  if (selection.shouldDeleteAnnotation()) {
    await selection.deleteAnnotation();
  }
}

async function handleGlobalLinkIndicatorClick(
  selectedAnnotation,
  instance,
  callback,
) {
  const selection = new Selection(selectedAnnotation.current, instance);

  callback && callback(selection.getCustomData());
}
